"use client";

import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { absoluteLocalizedUrl } from "@/lib/i18n/pageMetadata";
import {
  PhoneIcon,
  ClipboardDocumentCheckIcon,
  CheckCircleIcon,
  MapPinIcon,
  ArrowRightIcon,
  GiftIcon,
  ShieldCheckIcon,
  HomeModernIcon,
  BuildingOffice2Icon,
  ArchiveBoxIcon,
  CurrencyEuroIcon,
  StarIcon,
} from "@heroicons/react/24/outline";
import CtaLink from "@/components/ui/CtaLink";
import Contact from "@/components/Contact/Contact";
import { bruxellesData } from "@/lib/communesBruxelles";

const PHONE_TEL = "+32493966507";
const PHONE_DISPLAY = "+32 (0)493 96 65 07";

const SLUG_BY_NAME = Object.fromEntries(
  Object.entries(bruxellesData).map(([slug, data]) => [data.displayName, slug])
);

const RESOURCE_LINKS = [
  { icon: HomeModernIcon, key: "resourceDebarrasMaison", href: "/debarras-maison" },
  { icon: BuildingOffice2Icon, key: "resourceDebarrasAppartement", href: "/debarras-appartement" },
  { icon: ArchiveBoxIcon, key: "resourceCaveGrenierGarage", href: "/debarras-cave-grenier-garage" },
  { icon: CurrencyEuroIcon, key: "resourceTarifs", href: "/tarifs" },
  { icon: StarIcon, key: "resourceAvis", href: "/avis" },
  { icon: ClipboardDocumentCheckIcon, key: "resourceContact", href: "/contact" },
];

export default function PageClient({ commune, slug, locale }) {
  const t = useTranslations("CommunePage");
  const c = commune.content[locale];

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: c.faq.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: t("breadcrumbHome"),
        item: absoluteLocalizedUrl(locale, ""),
      },
      {
        "@type": "ListItem",
        position: 2,
        name: t("breadcrumbVideMaison"),
        item: absoluteLocalizedUrl(locale, "/vide-maison"),
      },
      {
        "@type": "ListItem",
        position: 3,
        name: c.displayName,
        item: absoluteLocalizedUrl(locale, `/vide-maison/${slug}`),
      },
    ],
  };

  return (
    <div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-sand text-ink">
        <div
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            background:
              "radial-gradient(900px 420px at 8% -10%, rgba(193,98,44,0.14), transparent 55%), radial-gradient(760px 360px at 100% 0%, rgba(63,108,81,0.12), transparent 55%)",
          }}
        />

        <div className="relative mx-auto max-w-[1200px] px-6 py-16 lg:px-10 lg:py-24">
          <nav className="text-xs text-ink/50">
            <Link href="/">{t("breadcrumbHome")}</Link> /{" "}
            <Link href="/vide-maison">{t("breadcrumbVideMaison")}</Link> /{" "}
            <span>{c.displayName}</span>
          </nav>

          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-ink/10 bg-white/70 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-ink/70">
            <MapPinIcon className="h-4 w-4 text-accent" />
            {commune.codePostal} — {commune.zone}
          </div>

          <h1 className="mt-5 max-w-3xl font-heading text-4xl font-bold leading-tight tracking-tight md:text-5xl">
            {c.heroTitle}
          </h1>

          <p className="mt-4 max-w-2xl text-lg font-semibold text-accent">
            {c.heroHighlight}
          </p>

          <p className="mt-4 max-w-2xl text-[16px] leading-8 text-ink/70">
            {c.intro}
          </p>

          <div className="mt-8 flex flex-wrap gap-4">
            <CtaLink href={`tel:${PHONE_TEL}`} variant="accent" icon={PhoneIcon}>
              {PHONE_DISPLAY}
            </CtaLink>
            <CtaLink href="#contactus" variant="sand" icon={ClipboardDocumentCheckIcon}>
              {t("freeQuote")}
            </CtaLink>
          </div>
        </div>
      </section>

      {/* CONTEXTE LOCAL */}
      <section className="mx-auto max-w-[1200px] px-6 py-16 lg:px-10">
        <h2 className="font-heading text-2xl font-bold tracking-tight md:text-3xl">
          {t("whatYouShouldKnow", { commune: c.displayName })}
        </h2>

        <p className="mt-5 max-w-3xl text-[16px] leading-8 text-ink/70">
          {c.localContext}
        </p>

        <p className="mt-4 max-w-3xl text-[16px] leading-8 text-ink/70">
          {c.serviceIntro}
        </p>

        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {[c.seoText1, c.seoText2, c.seoText3].map((text, i) => (
            <div
              key={i}
              className="rounded-2xl border border-sand-line bg-white p-5 text-sm leading-7 text-ink/70"
            >
              {text}
            </div>
          ))}
        </div>
      </section>

      {/* SITUATIONS */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
          <h2 className="font-heading text-2xl font-bold tracking-tight md:text-3xl">
            {c.situationsTitle}
          </h2>

          <ul className="mt-8 grid gap-4 md:grid-cols-2">
            {c.situations.map((situation, i) => (
              <li
                key={i}
                className="flex items-start gap-3 rounded-2xl border border-sand-line bg-sand/60 p-4"
              >
                <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-sage" />
                <span className="text-[15px] leading-7 text-ink/80">
                  {situation}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* RACHAT D'OBJETS */}
      <section className="mx-auto max-w-[1200px] px-6 py-16 lg:px-10">
        <div className="inline-flex items-center gap-2 rounded-full border border-sage/25 bg-sage/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-sage">
          <GiftIcon className="h-4 w-4" />
          {t("valuablesBadge")}
        </div>

        <h2 className="mt-4 font-heading text-2xl font-bold tracking-tight md:text-3xl">
          {c.valuablesTitle}
        </h2>

        <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {c.valuables.map((item, i) => (
            <li
              key={i}
              className="rounded-xl border border-sand-line bg-white px-4 py-3 text-sm font-medium text-ink/80"
            >
              {item}
            </li>
          ))}
        </ul>

        <Link
          href="/rachat-objets"
          className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-sage hover:underline"
        >
          {t("learnMoreRachat")}
          <ArrowRightIcon className="h-3.5 w-3.5" />
        </Link>
      </section>

      {/* TEXTES LONGS */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-[1200px] space-y-6 px-6 text-[16px] leading-8 text-ink/70 lg:px-10">
          <p>{c.longText1}</p>
          <p>{c.longText2}</p>
          <p>{c.longText3}</p>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="mx-auto max-w-[1200px] px-6 py-16 lg:px-10">
        <h2 className="font-heading text-2xl font-bold tracking-tight md:text-3xl">
          {t("whyChooseUs", { commune: c.displayName })}
        </h2>

        <ul className="mt-8 grid gap-4 md:grid-cols-2">
          {c.benefits.map((benefit, i) => (
            <li
              key={i}
              className="flex items-start gap-3 rounded-2xl border border-sand-line bg-sand/60 p-4"
            >
              <ShieldCheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
              <span className="text-[15px] leading-7 text-ink/80">
                {benefit}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* FAQ */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
          <h2 className="font-heading text-2xl font-bold tracking-tight md:text-3xl">
            {t("faqTitle")}
          </h2>

          <div className="mt-8 space-y-4">
            {c.faq.map((item, i) => (
              <div
                key={i}
                className="rounded-2xl border border-sand-line bg-sand/40 p-5"
              >
                <p className="font-semibold text-ink">{item.q}</p>
                <p className="mt-2 text-[15px] leading-7 text-ink/70">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RESSOURCES UTILES */}
      <section className="mx-auto max-w-[1200px] px-6 py-16 lg:px-10">
        <h2 className="font-heading text-2xl font-bold tracking-tight md:text-3xl">
          {t("usefulResources", { commune: c.displayName })}
        </h2>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {RESOURCE_LINKS.map(({ icon: Icon, key, href }) => (
            <Link
              key={key}
              href={href}
              className="group flex items-center gap-3 rounded-2xl border border-sand-line bg-white p-4 transition hover:border-accent/40 hover:shadow-premiumSm"
            >
              <Icon className="h-5 w-5 shrink-0 text-accent" />
              <span className="text-sm font-semibold text-ink/80 group-hover:text-ink">
                {t(key)}
              </span>
              <ArrowRightIcon className="ml-auto h-4 w-4 shrink-0 text-ink/30 transition group-hover:translate-x-0.5 group-hover:text-accent" />
            </Link>
          ))}
        </div>
      </section>

      {/* COMMUNES VOISINES */}
      {commune.nearby?.length > 0 && (
        <section className="bg-white py-16">
          <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
            <p className="text-sm font-semibold uppercase tracking-[0.14em] text-ink/50">
              {t("nearbyTitle")}
            </p>

            <div className="mt-4 flex flex-wrap gap-3">
              {commune.nearby.map((name) => {
                const nearbySlug = SLUG_BY_NAME[name];
                if (!nearbySlug) return null;
                const nearbyCommune = bruxellesData[nearbySlug];
                const nearbyDisplayName =
                  nearbyCommune.content[locale]?.displayName ??
                  nearbyCommune.displayName;

                return (
                  <Link
                    key={nearbySlug}
                    href={`/vide-maison/${nearbySlug}`}
                    className="rounded-full border border-sand-line bg-sand/60 px-4 py-2 text-sm font-medium text-ink/75 transition hover:border-accent/40 hover:text-ink"
                  >
                    {t("nearbyLinkPrefix")} {nearbyDisplayName}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <Contact />
    </div>
  );
}
